import React, {forwardRef, useImperativeHandle, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, cssVar, Input, List, ListItem, Spinner, Text, VisuallyHidden} from '@chakra-ui/react'
import useAutocomplete from './use-autocomplete'

export const $maxWidth = cssVar('max-width', '800px', 'autocomplete')

const Autocomplete = forwardRef(
    (
        {
            id = 'autocomplete',
            label,
            initialValue = '',
            suggestions: _suggestions = [],
            hideLabel,
            containerProps,
            dropdownProps,
            hideGroupTitles,
            groupTitleProps,
            optionProps,
            groupsListProps,
            optionsListProps,
            getOptionLabel = ({suggestion, optionIndex: _optionIndex, groupIndex: _groupIndex}) =>
                `${suggestion.label || suggestion.value || suggestion}`,
            renderContainer = (props) => <Box {...props} />,
            renderDropdown = (props) => <Box {...props} />,
            renderLabel = (props) => <Text {...props} />,
            renderInput = (props) => <Input {...props} />,
            renderGroup = ({groupIndex, ...props}) => <ListItem {...props} />,
            renderOptionsList = ({groupIndex, ...props}) => <List {...props} />,
            renderOption = ({isActive: _isActive, suggestion, optionIndex, groupIndex}) =>
                getOptionLabel({suggestion, optionIndex, groupIndex}),
            renderNoResults = () => <Text p={4}>No results found.</Text>,
            onSuggestionsFetchRequested,
            onSuggestionsClearRequested,
            onSuggestionSelected,
            isLoading = false,
            inputProps,
            multiSection
        },
        ref
    ) => {
        const suggestions = useMemo(
            () => (multiSection ? _suggestions : [{title: '', suggestions: _suggestions}]),
            [_suggestions, multiSection]
        )

        const resultsCount = useMemo(
            () =>
                suggestions?.reduce((acc, suggestion) => acc + suggestion.suggestions.length, 0) ??
                0,
            [suggestions]
        )

        const {
            getAnnouncementProps,
            getDescriptionProps,
            getDropdownProps,
            getLabelProps,
            getInputProps,
            getOptionProps,
            getOptionsListProps,
            isOpen,
            isOptionActive,
            setIsOpen,
            shouldShowDropdown
        } = useAutocomplete({
            getOptionLabel,
            groupedSuggestions: suggestions,
            id,
            initialValue,
            inputProps,
            isLoading,
            onSuggestionsFetchRequested,
            onSuggestionsClearRequested,
            onSuggestionSelected
        })

        useImperativeHandle(
            ref,
            () => ({
                isOpen,
                setIsOpen
            }),
            [isOpen, setIsOpen]
        )

        return renderContainer({
            position: {lg: 'relative'},
            width: 'full',
            ...containerProps,
            children: (
                <>
                    {renderLabel(
                        getLabelProps({
                            as: 'label',
                            display: hideLabel ? 'none' : null,
                            children: label
                        })
                    )}
                    {renderInput(getInputProps({}))}
                    <Text {...getDescriptionProps({display: 'none'})}>
                        Begin typing to search, use arrow keys to navigate, Enter to select
                    </Text>
                    <Text
                        {...getAnnouncementProps({
                            children: `${resultsCount} results found`,
                            display: 'none'
                        })}
                    />
                    {shouldShowDropdown &&
                        renderDropdown(
                            getDropdownProps({
                                position: 'absolute',
                                minWidth: '0',
                                width: 'full',
                                maxWidth: $maxWidth.reference,
                                marginTop: 1,
                                marginInline: 'auto',
                                // left and right are set to 0 to center the dropdown
                                left: '0',
                                right: '0',
                                zIndex: 'popover',
                                backgroundColor: 'stokkeCore.white',
                                boxShadow: 'dropdown',
                                padding: 8,
                                ...dropdownProps,

                                children: isLoading ? (
                                    <Box textAlign="center" p={4}>
                                        <Spinner />
                                    </Box>
                                ) : suggestions.length > 0 ? (
                                    <List {...getOptionsListProps({...groupsListProps})}>
                                        {suggestions.map((group, groupIndex) => {
                                            const headingId = `${id}-group-${groupIndex}-heading`
                                            return renderGroup({
                                                ...group.groupProps,
                                                groupIndex,
                                                key: groupIndex,
                                                role: 'group',
                                                'aria-labelledby': headingId,
                                                children: (
                                                    <>
                                                        {group.hideTitle || hideGroupTitles ? (
                                                            <VisuallyHidden id={headingId}>
                                                                {group.title}
                                                            </VisuallyHidden>
                                                        ) : (
                                                            <Text
                                                                {...groupTitleProps}
                                                                {...group.titleProps}
                                                                id={headingId}
                                                            >
                                                                {group.title}
                                                            </Text>
                                                        )}
                                                        {renderOptionsList({
                                                            ...optionsListProps,
                                                            ...group.optionsListProps,
                                                            groupIndex,
                                                            role: 'listbox',
                                                            'aria-labelledby': headingId,
                                                            children: (
                                                                <>
                                                                    {group.suggestions.map(
                                                                        (
                                                                            optionValue,
                                                                            optionIndex
                                                                        ) => {
                                                                            const isActive =
                                                                                isOptionActive(
                                                                                    groupIndex,
                                                                                    optionIndex
                                                                                )
                                                                            return (
                                                                                <ListItem
                                                                                    {...getOptionProps(
                                                                                        {
                                                                                            cursor: 'pointer',
                                                                                            _hover: {
                                                                                                bg: 'stokkeGray.mid'
                                                                                            },
                                                                                            bg: isActive
                                                                                                ? 'stokkeGray.mid'
                                                                                                : 'transparent',
                                                                                            ...optionProps,
                                                                                            ...group.optionProps,

                                                                                            groupIndex,
                                                                                            optionIndex
                                                                                        }
                                                                                    )}
                                                                                    key={
                                                                                        optionIndex
                                                                                    }
                                                                                >
                                                                                    {renderOption({
                                                                                        isActive,
                                                                                        suggestion:
                                                                                            optionValue,
                                                                                        optionIndex,
                                                                                        groupIndex
                                                                                    })}
                                                                                </ListItem>
                                                                            )
                                                                        }
                                                                    )}
                                                                </>
                                                            )
                                                        })}
                                                    </>
                                                )
                                            })
                                        })}
                                    </List>
                                ) : (
                                    renderNoResults()
                                )
                            })
                        )}
                </>
            )
        })
    }
)

if (process.env.NODE_ENV !== 'production') {
    Autocomplete.displayName = 'Autocomplete'
}

Autocomplete.propTypes = {
    id: PropTypes.string,
    initialValue: PropTypes.string,
    hideLabel: PropTypes.bool,
    containerProps: PropTypes.object,
    dropdownProps: PropTypes.object,
    hideGroupTitles: PropTypes.bool,
    groupTitleProps: PropTypes.object,
    optionProps: PropTypes.object,
    groupsListProps: PropTypes.object,
    optionsListProps: PropTypes.object,
    label: PropTypes.string,
    getOptionKey: PropTypes.func,
    getOptionLabel: PropTypes.func,
    renderContainer: PropTypes.func,
    renderInput: PropTypes.func,
    renderDropdown: PropTypes.func,
    renderGroup: PropTypes.func,
    renderLabel: PropTypes.func,
    renderNoResults: PropTypes.func,
    renderOptionsList: PropTypes.func,
    renderOption: PropTypes.func,
    suggestions: PropTypes.arrayOf(
        PropTypes.shape({
            groupProps: PropTypes.object,
            hideTitle: PropTypes.bool,
            title: PropTypes.string.isRequired,
            titleProps: PropTypes.object,
            optionsListProps: PropTypes.object,
            optionProps: PropTypes.object,
            suggestions: PropTypes.arrayOf(PropTypes.any).isRequired
        })
    ),
    onSuggestionsFetchRequested: PropTypes.func,
    onSuggestionsClearRequested: PropTypes.func,
    onSuggestionSelected: PropTypes.func,
    isLoading: PropTypes.bool,
    inputProps: PropTypes.object,
    multiSection: PropTypes.bool
}

export default Autocomplete
